// src/components/Common/SelectLawyer.jsx
import React, { useEffect, useMemo, useState } from "react";
import Select, { components } from "react-select";
import { getActiveLawyersLookup } from "../../services/lawyerService";

const STUDENT_CODE = "ESTUDIANTE";

const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";
const norm = (v) =>
  String(v || "")
    .trim()
    .toLowerCase();

const isStudent = (l) => {
  if (typeof l?.isStudent === "boolean") return l.isStudent;
  return (
    String(l?.academicLevelCode || "")
      .trim()
      .toUpperCase() === STUDENT_CODE
  );
};

// Badge “pill” para ESTUDIANTE
const StudentBadge = ({ isDarkMode }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 0.2,
      border: `1px solid ${isDarkMode ? "#14532d" : "#86efac"}`,
      background: isDarkMode ? "#052e16" : "#ecfdf5",
      color: isDarkMode ? "#86efac" : "#065f46",
      whiteSpace: "nowrap",
    }}
    title="Abogado estudiante (Laboratorio jurídico)"
  >
    ESTUDIANTE
  </span>
);

// Render custom: opción con badge + 2 líneas
const Option = (props) => {
  const { data, selectProps } = props;
  const { isDarkMode } = selectProps;

  const student = !!data?.data?.__isStudent;

  return (
    <components.Option {...props}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 700 }}>{data?.data?.__labelMain}</span>
          {student && <StudentBadge isDarkMode={isDarkMode} />}
        </div>

        {/* Subline opcional: email o texto */}
        {!isBlank(data?.data?.email) && (
          <span
            style={{
              fontSize: 12,
              opacity: 0.85,
            }}
          >
            {data.data.email}
          </span>
        )}
      </div>
    </components.Option>
  );
};

// Render custom: valor seleccionado con badge (más compacto)
const SingleValue = (props) => {
  const { data, selectProps } = props;
  const { isDarkMode } = selectProps;

  const student = !!data?.data?.__isStudent;

  return (
    <components.SingleValue {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontWeight: 700 }}>{data?.data?.__labelMain}</span>
        {student && <StudentBadge isDarkMode={isDarkMode} />}
      </div>
    </components.SingleValue>
  );
};

/**
 * Props:
 * - value: string (lawyerId) | null
 * - onChange: (lawyerId: string) => void
 * - isDisabled?: boolean
 * - placeholder?: string
 * - excludeIds?: string[]
 * - disableStudents?: boolean  // útil cuando estás seleccionando un Tutor
 */
const SelectLawyer = ({
  value,
  onChange,
  isDisabled = false,
  placeholder = "Seleccione un abogado...",
  excludeIds = [],
  disableStudents = false,
}) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const checkDarkMode = () =>
    document.body.classList.contains("dark-mode") ||
    document.querySelector(".app")?.classList.contains("dark-mode");

  useEffect(() => {
    setIsDarkMode(checkDarkMode());

    const targetNode = document.querySelector(".app") || document.body;
    const observer = new MutationObserver(() => setIsDarkMode(checkDarkMode()));

    observer.observe(targetNode, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchLawyers = async () => {
      try {
        setLoading(true);
        const res = await getActiveLawyersLookup();
        if (!mounted) return;

        if (res?.success && Array.isArray(res.data)) setOptions(res.data);
        else setOptions([]);
      } catch {
        if (mounted) setOptions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchLawyers();
    return () => {
      mounted = false;
    };
  }, []);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 38,
      backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
      borderColor: state.isFocused
        ? "#6c63ff"
        : isDarkMode
        ? "#444"
        : "#ced4da",
      boxShadow: state.isFocused
        ? "0 0 0 0.2rem rgba(108, 99, 255, 0.25)"
        : "none",
      color: isDarkMode ? "#fff" : "#000",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
      color: isDarkMode ? "#fff" : "#000",
      zIndex: 9999,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? "#fff" : "#000",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#6c63ff"
        : state.isFocused
        ? isDarkMode
          ? "#2c2c3e"
          : "#f0f0f0"
        : isDarkMode
        ? "#1e1e2f"
        : "#fff",
      color: isDarkMode ? "#fff" : "#000",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      opacity: state.isDisabled ? 0.55 : 1,
      fontSize: 14,
      paddingTop: 10,
      paddingBottom: 10,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? "#aaa" : "#888",
    }),
    input: (provided) => ({ ...provided, color: isDarkMode ? "#fff" : "#000" }),
  };

  const selectOptions = useMemo(() => {
    const excluded = new Set((excludeIds || []).filter(Boolean).map(norm));

    return (options || [])
      .filter((l) => !excluded.has(norm(l?.id)))
      .map((l) => {
        const student = isStudent(l);

        const labelMain = !isBlank(l?.documentNumber)
          ? `${l.documentNumber} - ${l.fullName}`
          : l?.fullName || "(Sin nombre)";

        return {
          value: l.id,
          label: labelMain, // react-select necesita un label string (aunque rendericemos custom)
          isDisabled: disableStudents && student,
          data: {
            ...l,
            __isStudent: student,
            __labelMain: labelMain,
          },
        };
      });
  }, [options, excludeIds, disableStudents]);

  const selectedOption = value
    ? selectOptions.find((o) => norm(o.value) === norm(value)) || null
    : null;

  return (
    <Select
      styles={customStyles}
      placeholder={placeholder}
      isClearable
      isDisabled={isDisabled || loading}
      isLoading={loading}
      menuPortalTarget={document.body}
      options={selectOptions}
      value={selectedOption}
      onChange={(selected) => onChange?.(selected?.value || "")}
      noOptionsMessage={() =>
        loading ? "Cargando abogados..." : "No se encontraron abogados activos"
      }
      components={{ Option, SingleValue }}
      // Pasamos esto para que los componentes custom lean el modo
      isDarkMode={isDarkMode}
    />
  );
};

export default SelectLawyer;
